/* @jsxImportSource react */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';

import { AuthFormError } from './AuthFormError';

/**
 * What the notice does the first time it mounts. The trigger belongs to the
 * container, not this component, so it's passed in rather than baked here:
 * - `send` — invoke `onResend` once (a magic-link sign-in that hasn't been sent
 *   yet), starting the cooldown only if that send succeeds.
 * - `cooldown` — a send already happened before this mounted (a set-up link, or
 *   a reset request), so just start the cooldown with no send.
 * - `idle` — do nothing.
 */
export type CheckEmailNoticeMount = 'send' | 'cooldown' | 'idle';

export interface CheckEmailNoticeProps {
  /** Heading copy, e.g. "Check your email". Owned by the container. */
  heading: React.ReactNode;
  /**
   * Body copy beneath the heading, e.g. the "We sent an email to …" line.
   * Optional so a container can withhold it — e.g. suppress the "sent"
   * confirmation until a send has actually succeeded, letting the error region
   * speak instead.
   */
  description?: React.ReactNode;
  /** Server-side failure message(s), rendered above the resend button. */
  error?: string | string[] | null;
  /**
   * The container's send call. Returns whether the send succeeded; the notice
   * starts (or restarts) the cooldown only on `true`. Must resolve rather than
   * throw — the notice awaits it inside the click handler.
   */
  onResend: () => Promise<boolean>;
  /** What to do on first mount. @default 'idle' */
  onMount?: CheckEmailNoticeMount;
  /** Resend cooldown length. @default 120 */
  cooldownSeconds?: number;
  /** Enabled resend button label; the countdown appends " in M:SS". @default 'Resend link' */
  resendLabel?: string;
  analyticsName: string;
  'data-testid'?: string;
}

const DEFAULT_COOLDOWN_SECONDS = 120;

/** `120` → `2:00`, `59` → `0:59`. */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * The shared "check your email" surface: a heading, a body-copy slot, an error
 * region, and a full-width resend button gated by a resend cooldown that counts
 * down in the label. Presentational — it owns the countdown-button behavior but
 * not the copy or the send: each container passes its own copy and its own
 * `onResend`, and declares the mount trigger via `onMount`. Composed by both
 * `MagicLinkModule` (sign-in / set-up) and the reset-password request module.
 */
export const CheckEmailNotice = ({
  heading,
  description,
  error,
  onResend,
  onMount = 'idle',
  cooldownSeconds = DEFAULT_COOLDOWN_SECONDS,
  resendLabel = 'Resend link',
  analyticsName,
  'data-testid': dataTestId,
}: CheckEmailNoticeProps) => {
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [sending, setSending] = useState(false);

  // Cooldown is derived from a monotonic-clock deadline, not a tick count:
  // `setInterval` is throttled to ~1 firing/min in a hidden tab (the whole point
  // of this screen is to switch to your mail app), so counting ticks would
  // stretch the cooldown far past its real duration. `performance.now()` is the
  // time source (immune to system-clock changes); the interval is only a nudge
  // to recompute and re-render.
  const deadlineRef = useRef(0);
  const startCooldown = useCallback(() => {
    deadlineRef.current = performance.now() + cooldownSeconds * 1000;
    setSecondsRemaining(cooldownSeconds);
  }, [cooldownSeconds]);

  const resend = useCallback(async () => {
    if (sending || secondsRemaining > 0) {
      return;
    }
    setSending(true);
    try {
      const succeeded = await onResend();
      if (succeeded) {
        startCooldown();
      }
    } catch {
      // A throwing `onResend` is treated as a failed send — no cooldown, the
      // button re-enables. `onResend` is contracted to resolve rather than
      // throw, but nothing enforces that, so catch here instead of leaking an
      // unhandled promise rejection out of the click handler / mount effect.
    } finally {
      setSending(false);
    }
  }, [sending, secondsRemaining, onResend, startCooldown]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) {
      return;
    }
    didMountRef.current = true;
    if (onMount === 'send') {
      void resend();
    } else if (onMount === 'cooldown') {
      startCooldown();
    }
    // Mount-only: the initial trigger keyed on `onMount`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cooldownActive = secondsRemaining > 0;
  useEffect(() => {
    if (!cooldownActive) {
      return undefined;
    }
    const recompute = (): void => {
      setSecondsRemaining(Math.max(0, Math.ceil((deadlineRef.current - performance.now()) / 1000)));
    };
    const intervalId = setInterval(recompute, 1000);
    // A hidden/suspended tab throttles the interval; recompute on refocus so the
    // label snaps to real elapsed time rather than however many ticks fired.
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        recompute();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [cooldownActive]);

  const handleResendClick = useCallback(() => {
    void resend();
  }, [resend]);

  const disabled = sending || secondsRemaining > 0;
  const label =
    secondsRemaining > 0 ? `${resendLabel} in ${formatCountdown(secondsRemaining)}` : resendLabel;

  return (
    <div data-testid={dataTestId} className="flex w-full flex-col gap-6">
      <div className="flex w-full flex-col gap-2">
        <Text as="h1" className="text-subheader-md text-od-content-primary">
          {heading}
        </Text>
        {description != null ? (
          <Text as="p" className="text-body-sm text-od-content-secondary">
            {description}
          </Text>
        ) : null}
      </div>
      <AuthFormError serverErrors={error} />
      <Button
        className="w-full"
        variant="secondary"
        aria-label={label}
        analyticsName={analyticsName}
        loading={sending}
        disabled={disabled}
        onClick={handleResendClick}
      >
        {label}
      </Button>
    </div>
  );
};

CheckEmailNotice.displayName = 'CheckEmailNotice';
